import { Fragment, type ReactNode } from "react";
import type { FeatureCollection, MultiPolygon, Polygon } from "geojson";

import type {
  AirDashStation,
  BangkokFloodWatch,
  CampusFloodEvent,
  CampusFloodStation,
  IncidentFeature,
  RainfallStation,
  TrafficCorridor,
  WaterGauge,
} from "@chula/shared";
import type { BuildingProperties, CctvCamera } from "../map/layers";
import type { useFeed } from "../hooks/useFeed";
import type { CampusEnergySnapshot } from "../lib/energyModel";
import type { LensId } from "../map/presets";

import { AqiBadge, type AqiTrend } from "./AqiBadge";
import { AirDashPanel } from "./AirDashPanel";
import { EnergyPanel } from "./EnergyPanel";
import { FloodWatchPanel } from "./FloodWatchPanel";
import { FloodPreventionPanel } from "./FloodPreventionPanel";
import { WeatherOutlookPanel } from "./WeatherOutlookPanel";
import { LiveCctvPanel } from "./LiveCctvPanel";
import { ParkingPanel } from "./ParkingPanel";
import { TrafficCorridorPanel } from "./TrafficCorridorPanel";
import { TrafficIncidentsPanel } from "./TrafficIncidentsPanel";
import { XrayLegend } from "./XrayLegend";

/**
 * LeftRail — the lens-scoped left sidebar of the map shell.
 *
 * Each lens declares which panels it carries. The shell renders
 * `<LeftRail lens={lens} ctx={ctx} />` and the rail looks up its slots.
 *
 * Why a config table rather than inline `lens === "..." ? ... : ...` JSX:
 *  - The same panels (FloodWatchPanel, AqiBadge, AirDashPanel) appear across
 *    multiple lenses with identical prop wiring. A config table lets the
 *    shared panels be defined once and reused.
 *  - Adding/removing/reordering a panel on a lens becomes a one-line edit.
 *  - The lens switch in App.tsx becomes a lookup, not a chain of ternaries.
 *  - Slot keys let React preserve panel state when the user moves between
 *    lenses that share a panel (FloodWatch survives ioc → maintenance).
 *
 * The "operational dashboard" rule (Fable 5, 0fd9f87): the rail carries
 * only what changes and what an operator acts on. Static reference content
 * (rankings, baseline, PMCU) lives in the Analytics tab.
 */

type FeedState<T> = ReturnType<typeof useFeed<T>>;

export type LeftRailCtx = {
  // Base URL for the API (passed to panels that own their own SWR cache
  // — currently only the flood prevention surface, which polls a custom
  // shape that doesn't fit useFeed<T[]>).
  apiBase: string;
  onOpenCameras?: () => void;

  // Live feeds (raw useFeed results — panels handle their own loading/tier).
  cctv: FeedState<CctvCamera>;
  floodWatch: FeedState<BangkokFloodWatch>;
  floodGauges: FeedState<WaterGauge>;
  floodRain: FeedState<RainfallStation>;
  floodCanals: FeedState<WaterGauge>;
  floodStations: FeedState<CampusFloodStation>;
  floodEvents: FeedState<CampusFloodEvent>;
  iticEvents: FeedState<IncidentFeature>;
  aqiTrend: FeedState<AqiTrend>;
  airDash: FeedState<AirDashStation>;
  trafficCorridors: FeedState<TrafficCorridor>;

  // Energy panel's modeled snapshot + the buildings index for click-to-fly.
  energySnapshot: CampusEnergySnapshot;
  buildings: FeatureCollection<Polygon | MultiPolygon, BuildingProperties> | null;

  // Actions
  flyTo: (lng: number, lat: number, zoom?: number) => void;
  cctvSamplingEnabled?: boolean;
  onSelectCctv: (camera: CctvCamera) => void;
  onToggleGhostMode: () => void;
  ghostMode: boolean;
};

type Slot = {
  key: string;
  render: (ctx: LeftRailCtx) => ReactNode;
};

// ── Slot definitions ────────────────────────────────────────────────────
// Stable module-level slots so React reconciliation can preserve panel
// state when switching lenses (the `key` is the identity anchor).

const cctvSlot: Slot = {
  key: "cctv",
  render: (ctx) => (
    <LiveCctvPanel
      cameras={ctx.cctv.data}
      samplingEnabled={ctx.cctvSamplingEnabled}
      loading={ctx.cctv.fallbackTier === "loading"}
      ageMinutes={ctx.cctv.ageMinutes}
      fallbackTier={ctx.cctv.fallbackTier === "loading" ? undefined : ctx.cctv.fallbackTier}
      onSelect={ctx.onSelectCctv}
      onRetry={ctx.cctv.refetch}
      apiBase={ctx.apiBase}
      onOpenCameras={ctx.onOpenCameras}
    />
  ),
};

const floodWatchSlot: Slot = {
  key: "flood",
  render: (ctx) => (
    <FloodWatchPanel
      watch={ctx.floodWatch.data[0] ?? null}
      gauges={ctx.floodGauges.data}
      rain={ctx.floodRain.data}
      canals={ctx.floodCanals.data}
      loading={ctx.floodWatch.fallbackTier === "loading"}
      ageMinutes={ctx.floodWatch.ageMinutes}
      fallbackTier={ctx.floodWatch.fallbackTier === "loading" ? undefined : ctx.floodWatch.fallbackTier}
      stations={ctx.floodStations.data}
      events={ctx.floodEvents.data}
      onFlyTo={(lng, lat) => ctx.flyTo(lng, lat, 16)}
      onRetry={ctx.floodWatch.refetch}
    />
  ),
};

// Campus-area prevention: Pathumwan + adjacent districts. Sits next to
// the flood watch so the operator sees "what's happening in Bangkok" and
// "what's happening near us" side by side. Owns its own SWR poll because
// it composes upstream data that doesn't fit the per-feed useFeed shape.
const floodPreventionSlot: Slot = {
  key: "flood-prevention",
  render: (ctx) => (
    <FloodPreventionPanel apiBase={ctx.apiBase} />
  ),
};

// Hourly + 7-day outlook with tomorrow's rain called out, plus FloodDash's own
// precision/recall for its flood warning.
//
// SLOT ORDER IS PER-LENS ON PURPOSE. Measured on the live rail at 1680×1050:
// the panel is 259px tall and the rail shows 841px, so in the last position it
// started at y=934 with the fold at 954 — 20px visible, the rest behind a
// scroll. No amount of shrinking fixes that; only moving it does. On the IOC
// lens it therefore sits directly under the camera wall, where "how much rain
// tomorrow" and "how much should I trust a flood warning" are the lead
// questions for an executive, and both are readable without scrolling.
//
// The operator lenses keep it after flood prevention instead, beside the
// near-campus detail it actually informs. Order there is unchanged from when
// the panel landed, so no existing panel moved on those lenses.
const weatherOutlookSlot: Slot = {
  key: "weather-outlook",
  render: (ctx) => (
    <WeatherOutlookPanel apiBase={ctx.apiBase} />
  ),
};

const aqiSlot: Slot = {
  key: "aqi",
  render: (ctx) => (
    <AqiBadge
      trend={ctx.aqiTrend.data[0] ?? null}
      loading={ctx.aqiTrend.fallbackTier === "loading"}
      ageMinutes={ctx.aqiTrend.ageMinutes}
      fallbackTier={ctx.aqiTrend.fallbackTier === "loading" ? undefined : ctx.aqiTrend.fallbackTier}
    />
  ),
};

const trafficIncidentsSlot: Slot = {
  key: "traffic-events",
  render: (ctx) => (
    <TrafficIncidentsPanel
      events={ctx.iticEvents.data}
      loading={ctx.iticEvents.fallbackTier === "loading"}
      ageMinutes={ctx.iticEvents.ageMinutes}
      fallbackTier={ctx.iticEvents.fallbackTier === "loading" ? undefined : ctx.iticEvents.fallbackTier}
      onFlyTo={(lng, lat) => ctx.flyTo(lng, lat, 17)}
      onRetry={ctx.iticEvents.refetch}
    />
  ),
};

const airDashSlot: Slot = {
  key: "air-dash",
  render: (ctx) => (
    <AirDashPanel
      stations={ctx.airDash.data}
      loading={ctx.airDash.fallbackTier === "loading"}
      ageMinutes={ctx.airDash.ageMinutes}
      fallbackTier={ctx.airDash.fallbackTier === "loading" ? undefined : ctx.airDash.fallbackTier}
      onFlyTo={(lng, lat) => ctx.flyTo(lng, lat, 15)}
      onRetry={ctx.airDash.refetch}
    />
  ),
};

const trafficCorridorsSlot: Slot = {
  key: "corridors",
  render: (ctx) => (
    <TrafficCorridorPanel
      corridors={ctx.trafficCorridors.data}
      ageMinutes={ctx.trafficCorridors.ageMinutes}
      fallbackTier={ctx.trafficCorridors.fallbackTier}
    />
  ),
};

const parkingSlot: Slot = {
  key: "parking",
  render: () => <ParkingPanel />,
};

const xrayLegendSlot: Slot = {
  key: "xray-legend",
  render: () => <XrayLegend />,
};

const energyPanelSlot: Slot = {
  key: "energy",
  render: (ctx) => (
    <EnergyPanel
      snapshot={ctx.energySnapshot}
      ghostMode={ctx.ghostMode}
      onToggleGhostMode={ctx.onToggleGhostMode}
      onSelectBuilding={(b) => {
        const f = ctx.buildings?.features.find((x) => (x.properties.id ?? "") === b.buildingId);
        if (f) {
          const c = f.geometry.type === "Polygon"
            ? f.geometry.coordinates[0][0]
            : f.geometry.coordinates[0][0][0];
          if (c) ctx.flyTo(c[0], c[1], 18);
        }
      }}
    />
  ),
};

// ── Lens → slot table ───────────────────────────────────────────────────
// One row per lens. The order here is the order panels render top-to-bottom.

const SLOTS_BY_LENS: Record<LensId, readonly Slot[]> = {
  ioc: [cctvSlot, weatherOutlookSlot, floodWatchSlot, floodPreventionSlot],
  maintenance: [aqiSlot, trafficIncidentsSlot, floodWatchSlot, floodPreventionSlot, weatherOutlookSlot, airDashSlot],
  energy: [energyPanelSlot],
  mobility: [aqiSlot, floodWatchSlot, floodPreventionSlot, weatherOutlookSlot, airDashSlot, trafficCorridorsSlot, parkingSlot],
  xray: [xrayLegendSlot],
};

export function LeftRail({ lens, ctx }: { lens: LensId; ctx: LeftRailCtx }) {
  const slots = SLOTS_BY_LENS[lens];
  return (
    <aside className={`left-bar ${lens === "ioc" ? "left-bar-exec" : ""}`} aria-label="Lens panels">
      {slots.map((slot) => (
        <Fragment key={slot.key}>{slot.render(ctx)}</Fragment>
      ))}
    </aside>
  );
}

/** Exported for tests + the Analytics tab. */
export const LEFT_RAIL_SLOTS_BY_LENS = SLOTS_BY_LENS;
export type { Slot as LeftRailSlot };
